import { useEffect, useRef, useState } from "react";
import {
  subscribeToBookingRequest,
  trackBookingRequest,
} from "../lib/supabase";
import { getRecentTrackingTokens } from "../lib/storage";
import { logWarn } from "../lib/logger";

type SnapshotMap = Record<string, { status?: string; progress?: string }>;

export function useBookingToasts(
  showToast: (title: string, message?: string) => void
) {
  const [trackedTokens, setTrackedTokens] = useState<string[]>([]);
  const [, setLastSnapshots] = useState<SnapshotMap>({});
  const lastSnapshotsRef = useRef<SnapshotMap>({});
  const showToastRef = useRef(showToast);

  // Keep showToast ref up to date
  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  // Sync tracked tokens from localStorage
  useEffect(() => {
    const syncTokens = () => {
      setTrackedTokens(getRecentTrackingTokens());
    };
    syncTokens();
    window.addEventListener("storage", syncTokens);
    window.addEventListener("fourm:tokens-updated", syncTokens);
    return () => {
      window.removeEventListener("storage", syncTokens);
      window.removeEventListener("fourm:tokens-updated", syncTokens);
    };
  }, []);

  // Subscribe to realtime updates for tracked tokens
  useEffect(() => {
    const cleanups =
      trackedTokens?.slice(0, 5).map((token) =>
        subscribeToBookingRequest(token, (payload: any) => {
          if (payload?.success) {
            const status = payload?.request?.status;
            const progress = payload?.booking?.progressStatus;
            const title = "تحديث حالة الطلب";
            const message = progress
              ? `التقدم: ${progress}`
              : status
              ? `الحالة: ${status}`
              : "تم تحديث بيانات الطلب";
            showToastRef.current(title, message);
            setLastSnapshots((prev) => {
              const next = {
                ...prev,
                [token]: {
                  status,
                  progress,
                },
              };
              lastSnapshotsRef.current = next;
              return next;
            });
          }
        })
      ) || [];

    return () => {
      cleanups.forEach((fn) => {
        if (typeof fn === "function") fn();
      });
    };
  }, [trackedTokens]);

  // Poll for updates (fallback to realtime)
  useEffect(() => {
    if (!trackedTokens?.length) return undefined;

    let cancelled = false;

    const poll = async () => {
      const tokens = trackedTokens.slice(0, 5);
      await Promise.all(
        tokens.map(async (token) => {
          try {
            const { data, error } = await trackBookingRequest(token);
            if (cancelled) return;
            if (error || data?.success === false) {
              return;
            }
            const snapshot = data?.data ?? data;
            const status = snapshot?.request?.status;
            const progress = snapshot?.booking?.progressStatus;
            const prev = lastSnapshotsRef.current[token];
            if (
              prev &&
              (prev.status !== status || prev.progress !== progress)
            ) {
              const title = "تحديث حالة الطلب";
              const message = progress
                ? `التقدم: ${progress}`
                : status
                ? `الحالة: ${status}`
                : "تم تحديث بيانات الطلب";
              showToastRef.current(title, message);
            }
            setLastSnapshots((prevMap) => {
              const next = {
                ...prevMap,
                [token]: {
                  status,
                  progress,
                },
              };
              lastSnapshotsRef.current = next;
              return next;
            });
          } catch (err: any) {
            logWarn("Poll error", err, "toast");
          }
        })
      );
    };

    poll();
    const id = setInterval(poll, 20000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [trackedTokens]);

  return { trackedTokens };
}
